/**
 * financials-constants.ts — SIL-291 (W0, financials-hifi-flow-spec.md
 * Cross-cutting) — the single home for every numeric threshold used across
 * the financials-hifi-flow epic (zombie/stale detection, impairment tiers,
 * comps k-anonymity, sensitivity bounds, bulk-write caps). No slice's
 * implementation may hardcode one of these values independently — import
 * from here so a future policy change (e.g. "zombie dark threshold becomes
 * per-org configurable") has exactly one call site to touch.
 *
 * `BASIS_CONSTANT_REGISTRY` doubles as the allowlist backing
 * `dto/financials.ts`'s `BasisConstant` union — every constant that is ever
 * legitimately emittable inside a `CalculationBasis.constants[]` entry is
 * registered here, and the DTO's `z.enum(...)` is generated from these keys.
 * A constant NOT registered here cannot appear in a basis payload — the
 * allowlist is enforced by construction, not by a second list someone has to
 * remember to keep in sync.
 */

import type { BasisSource } from './dto/financials.js';
import { TDP_DUTY_CYCLE_FALLBACK_RATIO, HOST_OVERHEAD_FACTOR } from './power-cost.js';

// ---------------------------------------------------------------------------
// Data Health (spec slice 2) — anomaly detection thresholds
// ---------------------------------------------------------------------------

/** Zombie anomaly: net book > 0, lifecycle active, dark in monitoring >= N days. */
export const ZOMBIE_DARK_DAYS_THRESHOLD = 30;

/** Stale telemetry: no data >= N days — matches the existing scoring cutoff. */
export const STALE_TELEMETRY_DAYS_THRESHOLD = 7;

// ---------------------------------------------------------------------------
// Impairment watchlist (spec slice 5) — FMV-vs-book tier thresholds
// ---------------------------------------------------------------------------

/** WATCH tier: FMV >= this % below net book value. */
export const IMPAIRMENT_WATCH_PCT = 10;

/** REVIEW tier: FMV >= this % below book, OR REVIEW_MIN_QUARTERS+ consecutive quarters. */
export const IMPAIRMENT_REVIEW_PCT = 20;
export const IMPAIRMENT_REVIEW_MIN_QUARTERS = 2;

/** LIKELY tier: FMV >= this % below book AND LIKELY_MIN_QUARTERS+ consecutive quarters. */
export const IMPAIRMENT_LIKELY_PCT = 30;
export const IMPAIRMENT_LIKELY_MIN_QUARTERS = 2;

// ---------------------------------------------------------------------------
// Book vs FMV "Suggested action" (spec §4 By-Asset-Class table) — trend-change
// bands. These were literals inside book-vs-fmv.tsx until the basis-disclosure
// retrofit; they live here because a `CalculationBasis.constants[]` entry may
// only name a registered constant, and because a threshold that decides
// "Sell soon" is exactly the kind of value that must not be per-call-site.
// ---------------------------------------------------------------------------

/** SELL SOON band: 18-month FMV change at or below this percent. */
export const SUGGESTED_ACTION_SELL_SOON_PCT = -15;

/** MONITOR CLOSELY band: change at or below this percent (and above SELL_SOON). */
export const SUGGESTED_ACTION_MONITOR_PCT = -5;

/** HOLD / RISING band: change at or above this percent. Between MONITOR and
 * this value the class reads as stable. */
export const SUGGESTED_ACTION_RISING_PCT = 5;

// ---------------------------------------------------------------------------
// Cross-cutting — market-comp disclosure policy
// ---------------------------------------------------------------------------

/**
 * k-anonymity floor for any comps-derived basis value (median, p25/p75,
 * count) — suppressed below this count. Applies everywhere comps surface:
 * market-trends, FMV basis, sensitivity street-price bounds, exports.
 */
export const COMPS_K_ANONYMITY_FLOOR = 5;

// ---------------------------------------------------------------------------
// Market Intelligence purchase-decision (SIL-1069/1070/1071,
// docs/superpowers/specs/2026-09-16-market-intelligence-design.md) --
// sell-timing horizon cap.
// ---------------------------------------------------------------------------

/**
 * Purchase-analysis sell-timing horizon cap, in months. Twelve is a platform
 * cap, not a user setting: the margin engine's argmax walk (`purchase-
 * engine.ts`) evaluates hold month 0..PURCHASE_MAX_HOLD_MONTHS, and
 * `db/schema/895_client_sku_fmv_forecast_view.sql` caps the client-safe FMV
 * forecast view at the same horizon (mirrored via a SQL comment naming this
 * constant, since SQL cannot import it directly) -- change this in exactly
 * one place, then update the SQL migration comment and the view definition
 * together, or the two drift.
 */
export const PURCHASE_MAX_HOLD_MONTHS = 12;

// ---------------------------------------------------------------------------
// Scenario sensitivity (spec slice 4) — tornado attribute bounds
// ---------------------------------------------------------------------------

/** Power $/kWh sensitivity swing, +/- percent. */
export const SENSITIVITY_KWH_BOUND_PCT = 25;

/** Residual recovery % sensitivity swing, +/- percentage points. */
export const SENSITIVITY_RESIDUAL_RECOVERY_BOUND_PTS = 10;

/** PUE sensitivity swing, +/- percent, clamped into [1.0, 3.0] (SIL-346,
 * T8). PUE is the one tornado attribute that is a genuine "electricity
 * input" shared by BOTH the dollar power-cost math (`scenario-engine.ts`
 * already multiplies `pue` into `powerCostOverMonths`) and the use-phase
 * carbon projection (`applyPue`) — perturbing it is required to move both
 * together, unlike `power_kwh` ($/kWh price), which only ever affects
 * dollars. */
export const SENSITIVITY_PUE_BOUND_PCT = 15;

// ---------------------------------------------------------------------------
// Carbon sensitivity (SIL-443, W5 of
// resources/plans/sustainability-forecasting-spec.md §11 "ship last") —
// bounds for the CARBON tornado's drivers. Deliberately a separate set from
// the dollar tornado's bounds above: these perturb physical/modeling inputs
// to the SIL-435 carbon kernel (grid factor, adder scalars), not price
// inputs, and their plausible ranges have nothing to do with each other.
// PUE is the one shared driver and reuses SENSITIVITY_PUE_BOUND_PCT above
// rather than declaring a second, silently-divergent PUE bound.
// ---------------------------------------------------------------------------

/**
 * Grid-intensity-factor swing, +/- percent. Wide (relative to the dollar
 * bounds) on purpose: it stands in for BOTH cross-country factor dispersion
 * and the disclosed "grid intensity held constant over the horizon" caveat
 * (copy §3) — the single largest modeling uncertainty in the operational leg,
 * and the reason this row usually dominates the tornado.
 */
export const CARBON_SENSITIVITY_GRID_BOUND_PCT = 30;

/**
 * Swing applied to the org's SIL-420 adder scalars (`disposal_weee_factor`,
 * `used_transport_km`, `used_refurb_pct`), +/- percent. One bound for all
 * three: they are the same KIND of input (a platform research default the org
 * may override), and giving each its own hand-tuned percentage would imply a
 * per-scalar confidence this platform has not measured.
 */
export const CARBON_SENSITIVITY_ADDER_BOUND_PCT = 50;

// ---------------------------------------------------------------------------
// Tenant-isolation & authz invariants (restated) — bulk write caps
// ---------------------------------------------------------------------------

/** Max rows accepted by a bulk asset-financial entry request. */
export const BULK_ENTRY_MAX_COUNT = 500;

// ---------------------------------------------------------------------------
// CalculationBasis constant allowlist (Cross-cutting: Level-2 basis disclosure)
// ---------------------------------------------------------------------------

/**
 * Every constant a `CalculationBasis.constants[]` entry may legitimately
 * name. All are `platform_default`-sourced (they are code constants, not
 * data resolved from a request) — a per-org materiality override (deferred,
 * spec §5) would need its own source tag when it ships.
 */
export const BASIS_CONSTANT_REGISTRY = {
  zombieDarkDaysThreshold: { value: ZOMBIE_DARK_DAYS_THRESHOLD, source: 'platform_default' as BasisSource },
  staleTelemetryDaysThreshold: {
    value: STALE_TELEMETRY_DAYS_THRESHOLD,
    source: 'platform_default' as BasisSource,
  },
  impairmentWatchPct: { value: IMPAIRMENT_WATCH_PCT, source: 'platform_default' as BasisSource },
  impairmentReviewPct: { value: IMPAIRMENT_REVIEW_PCT, source: 'platform_default' as BasisSource },
  impairmentReviewMinQuarters: {
    value: IMPAIRMENT_REVIEW_MIN_QUARTERS,
    source: 'platform_default' as BasisSource,
  },
  impairmentLikelyPct: { value: IMPAIRMENT_LIKELY_PCT, source: 'platform_default' as BasisSource },
  impairmentLikelyMinQuarters: {
    value: IMPAIRMENT_LIKELY_MIN_QUARTERS,
    source: 'platform_default' as BasisSource,
  },
  suggestedActionSellSoonPct: {
    value: SUGGESTED_ACTION_SELL_SOON_PCT,
    source: 'platform_default' as BasisSource,
  },
  suggestedActionMonitorPct: {
    value: SUGGESTED_ACTION_MONITOR_PCT,
    source: 'platform_default' as BasisSource,
  },
  suggestedActionRisingPct: {
    value: SUGGESTED_ACTION_RISING_PCT,
    source: 'platform_default' as BasisSource,
  },
  compsKAnonymityFloor: { value: COMPS_K_ANONYMITY_FLOOR, source: 'platform_default' as BasisSource },
  sensitivityKwhBoundPct: { value: SENSITIVITY_KWH_BOUND_PCT, source: 'platform_default' as BasisSource },
  sensitivityResidualRecoveryBoundPts: {
    value: SENSITIVITY_RESIDUAL_RECOVERY_BOUND_PTS,
    source: 'platform_default' as BasisSource,
  },
  sensitivityPueBoundPct: { value: SENSITIVITY_PUE_BOUND_PCT, source: 'platform_default' as BasisSource },
  // SIL-443 (W5) — carbon tornado bounds.
  carbonSensitivityGridBoundPct: {
    value: CARBON_SENSITIVITY_GRID_BOUND_PCT,
    source: 'platform_default' as BasisSource,
  },
  carbonSensitivityAdderBoundPct: {
    value: CARBON_SENSITIVITY_ADDER_BOUND_PCT,
    source: 'platform_default' as BasisSource,
  },
  bulkEntryMaxCount: { value: BULK_ENTRY_MAX_COUNT, source: 'platform_default' as BasisSource },
  // SIL-351 — the wattage-ladder's duty-cycle + host-overhead multipliers
  // (power-cost.ts), citable on any basis that discloses a nameplate-derived
  // power-cost figure.
  tdpDutyCycleFallbackRatio: { value: TDP_DUTY_CYCLE_FALLBACK_RATIO, source: 'platform_default' as BasisSource },
  hostOverheadFactor: { value: HOST_OVERHEAD_FACTOR, source: 'platform_default' as BasisSource },
} as const satisfies Record<string, { value: number; source: BasisSource }>;

export type BasisConstantName = keyof typeof BASIS_CONSTANT_REGISTRY;
